import classNames from "classnames"

import { getOrderClass } from "../../../utils/layout"

import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import React, { ReactNode, ElementType, forwardRef, useCallback } from "react"

type LayoutItemProps<T extends ElementType> = {
  mobileGridOrder?: number
  as?: T
  children?: ReactNode
  className?: string
  "data-testid"?: string
  /**
   * Fades the item up into view the first time it scrolls into the viewport.
   * Only takes effect if `as` resolves to a component/tag that forwards its
   * ref to a real DOM node (a native intrinsic tag, or a `forwardRef`
   * component) — otherwise this silently no-ops. Never combine with a
   * non-forwardRef component (e.g. PillButton).
   */
  animateOnScroll?: boolean
  /**
   * Forwarded to the scroll-animation hook; only meaningful when
   * `animateOnScroll` is true.
   */
  isSliderChild?: boolean
} & React.ComponentPropsWithRef<T>

// No mergeRefs utility exists elsewhere in the codebase; this is the only
// call site, so it's kept local rather than extracted as a shared utility.
const setRef = <T,>(ref: React.ForwardedRef<T>, value: T | null): void => {
  if (typeof ref === "function") {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

/**
 * Shared polymorphic component for all direct children of a layout
 *
 * Used to ensure any required attributes are
 * added to children of a layout (ie: mobile grid order)
 */
const LayoutItem = forwardRef(function LayoutItem<
  T extends ElementType = "div",
>(
  {
    mobileGridOrder,
    as,
    children,
    className,
    "data-testid": dataTestId,
    animateOnScroll = false,
    isSliderChild = false,
    ...props
  }: LayoutItemProps<T>,
  ref: React.ForwardedRef<Element>
) {
  const Component = as || "div" // Fallback needs to explicitly be set here

  // We cant use style prop here because some components
  // take "style" as a string union (ie: PillButton).
  // so we have to use tailwind classes with
  // string interpolation for order
  // - see tailwind config for safelist
  const orderClass = mobileGridOrder && getOrderClass(mobileGridOrder)

  const animationRefCallback = useScrollAnimateSelf(
    animateOnScroll,
    isSliderChild
  )

  const mergedRef = useCallback(
    (node: Element | null) => {
      setRef(ref, node)
      // animationRefCallback needs HTMLElement; node is Element | null.
      // Real narrowing, not a cast - SVGElement/etc. are intentionally
      // excluded, matching the hook's own HTMLElement contract.
      if (node === null || node instanceof HTMLElement) {
        animationRefCallback(node)
      }
    },
    [ref, animationRefCallback]
  )

  return (
    <Component
      ref={mergedRef}
      data-testid={dataTestId}
      className={classNames(
        orderClass,
        {
          "sm:!order-none": mobileGridOrder,
        },
        className
      )}
      {...props}
    >
      {children}
    </Component>
  ) // This assertion is needed for prop type inference on polymorphic components
}) as <T extends ElementType = "div">(
  props: LayoutItemProps<T> & { ref?: React.ForwardedRef<HTMLElement | null> }
) => React.ReactElement | null

export default LayoutItem
